import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, RefreshCw, Search, ShieldCheck, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { adminApi } from "../../api/admin";
import { useAuth } from "../../auth/AuthContext";
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  DataTable,
  Drawer,
  EmptyState,
  PageHeader,
  SearchInput,
  Segmented,
  Switch,
  formatDateTime,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import type { AdminUser } from "../../types";
import { PERSON_ROLES, ROLE_INFO, errorText, roleLabel } from "./labels";

type Filter = "all" | "admins" | "norole" | "disabled";

const ADMIN_ROLES = new Set(["platform_admin", "report_admin"]);

function sortRoles(roles: string[]): string[] {
  const rank = (r: string) => {
    const i = PERSON_ROLES.indexOf(r);
    return i === -1 ? 99 : i;
  };
  return [...roles].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

function matches(u: AdminUser, f: Filter): boolean {
  if (f === "admins") return u.roles.some((r) => ADMIN_ROLES.has(r));
  if (f === "norole") return u.roles.filter((r) => r !== "device").length === 0;
  if (f === "disabled") return !u.is_active;
  return true;
}

function nameOf(u: AdminUser): string {
  return u.display_name?.trim() || u.email;
}

export function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user: me, hasRole } = useAuth();
  const canManageRoles = hasRole("platform_admin");

  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers });
  const roles = useQuery({ queryKey: ["admin", "roles"], queryFn: adminApi.listRoles });
  const tenants = useQuery({ queryKey: ["admin", "tenants"], queryFn: adminApi.listTenants, enabled: canManageRoles });

  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const orgName = useMemo(() => new Map((tenants.data ?? []).map((t) => [t.tenant_id, t.display_name])), [tenants.data]);
  const all = users.data ?? [];
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: all.length, admins: 0, norole: 0, disabled: 0 };
    for (const u of all) (["admins", "norole", "disabled"] as Filter[]).forEach((f) => matches(u, f) && c[f]++);
    return c;
  }, [all]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((u) => matches(u, filter))
      .filter((u) => !q || [u.display_name, u.email, orgName.get(u.entra_tenant_id) ?? "", ...u.roles.map(roleLabel)].join(" ").toLowerCase().includes(q));
  }, [all, filter, search, orgName]);

  const setActive = useMutation({
    mutationFn: ({ u, active }: { u: AdminUser; active: boolean }) => adminApi.setUserActive(u.id, active),
    onSuccess: (_r, { u, active }) => {
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
      toast.success(active ? "Account turned back on" : "Account disabled", active ? `${nameOf(u)} can sign in again.` : `${nameOf(u)} can no longer sign in.`);
    },
    onError: (e) => toast.error("That didn't work", errorText(e)),
  });

  const toggleActive = async (u: AdminUser, active: boolean) => {
    if (!active) {
      const ok = await confirm({
        title: `Disable ${nameOf(u)}?`,
        message: "They lose access straight away and can't sign in until you turn their account back on. Their reports and history are kept.",
        confirmLabel: "Disable account",
        tone: "danger",
      });
      if (!ok) return;
    }
    setActive.mutate({ u, active });
  };

  const open = all.find((u) => u.id === openId) ?? null;
  const assignable = useMemo(() => {
    const fromApi = (roles.data ?? []).filter((r) => r !== "device");
    return sortRoles(fromApi.length ? fromApi : PERSON_ROLES);
  }, [roles.data]);

  return (
    <div className="ds-page">
      <PageHeader
        title="People & roles"
        subtitle="Everyone who has signed in with their Microsoft 365 account. People appear here after their first sign-in — then you choose what they can do."
        actions={
          <Button icon={Building2} onClick={() => navigate("/admin/tenants")}>
            Organizations
          </Button>
        }
      />

      {users.isError ? (
        <Banner tone="danger" title="People couldn't be loaded" action={<Button size="sm" icon={RefreshCw} onClick={() => void users.refetch()}>Try again</Button>}>
          {errorText(users.error)}
        </Banner>
      ) : (
        <>
          <div className="ds-toolbar">
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email or role" width={300} />
            <Segmented<Filter>
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: `Everyone ${counts.all}` },
                { value: "admins", label: `Admins ${counts.admins}` },
                ...(counts.norole ? [{ value: "norole" as Filter, label: `No role ${counts.norole}`, title: "Can sign in but can't see anything yet" }] : []),
                { value: "disabled", label: `Disabled ${counts.disabled}` },
              ]}
            />
          </div>
          {counts.norole && filter !== "norole" ? (
            <div style={{ marginBottom: 16 }}>
              <Banner
                tone="info"
                title={`${counts.norole} ${counts.norole === 1 ? "person has" : "people have"} no role yet`}
                action={<Button size="sm" onClick={() => setFilter("norole")}>Show them</Button>}
              >
                They can sign in but won't see any reports until you give them a role.
              </Banner>
            </div>
          ) : null}
          <Card flush>
            <DataTable<AdminUser>
              rows={users.data ? rows : undefined}
              loading={users.isLoading}
              rowKey={(u) => u.id}
              onRowClick={(u) => setOpenId(u.id)}
              initialSort={{ key: "name", dir: "asc" }}
              empty={
                all.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="No one has signed in yet"
                    description="People appear here after they sign in with their Microsoft 365 account for the first time. Make sure their organization is allowed first."
                    action={<Button variant="primary" icon={Building2} onClick={() => navigate("/admin/tenants")}>Check organizations</Button>}
                  />
                ) : (
                  <EmptyState
                    icon={Search}
                    title="No one matches"
                    description="Try another search or filter."
                    action={
                      <Button
                        onClick={() => {
                          setSearch("");
                          setFilter("all");
                        }}
                      >
                        Clear filters
                      </Button>
                    }
                  />
                )
              }
              columns={[
                {
                  key: "name",
                  header: "Person",
                  width: "32%",
                  sortValue: (u) => nameOf(u),
                  cell: (u) => (
                    <div className="ds-row" style={{ gap: 10, minWidth: 0 }}>
                      <Avatar name={nameOf(u)} />
                      <div style={{ minWidth: 0 }}>
                        <div className="ds-row" style={{ gap: 6 }}>
                          <span className="ds-truncate" style={{ fontWeight: 600 }}>{nameOf(u)}</span>
                          {me?.id === u.id ? <Badge>You</Badge> : null}
                        </div>
                        <div className="ds-muted ds-small ds-truncate">{u.email}</div>
                      </div>
                    </div>
                  ),
                },
                ...(orgName.size
                  ? [
                      {
                        key: "org",
                        header: "Organization",
                        sortValue: (u: AdminUser) => orgName.get(u.entra_tenant_id) ?? "",
                        cell: (u: AdminUser) => <span className="ds-muted">{orgName.get(u.entra_tenant_id) ?? "Unknown"}</span>,
                      },
                    ]
                  : []),
                {
                  key: "roles",
                  header: "Roles",
                  sortValue: (u) => sortRoles(u.roles).map((r) => PERSON_ROLES.indexOf(r)).join(","),
                  cell: (u) => {
                    const list = sortRoles(u.roles.filter((r) => r !== "device"));
                    return list.length ? (
                      <div className="ds-row" style={{ gap: 4, flexWrap: "wrap" }}>
                        {list.map((r) => (
                          <Badge key={r} tone={ROLE_INFO[r]?.tone ?? "neutral"} title={ROLE_INFO[r]?.description}>
                            {roleLabel(r)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <Badge tone="warning" title="Can sign in but can't see anything yet">No role</Badge>
                    );
                  },
                },
                {
                  key: "login",
                  header: "Last sign-in",
                  sortValue: (u) => u.last_login_at ?? "",
                  cell: (u) =>
                    u.last_login_at ? <span title={formatDateTime(u.last_login_at)}>{formatRelative(u.last_login_at)}</span> : <span className="ds-muted">Not recorded</span>,
                },
                {
                  key: "active",
                  header: "Can sign in",
                  sortValue: (u) => (u.is_active ? 0 : 1),
                  cell: (u) => {
                    const self = me?.id === u.id;
                    return (
                      <span onClick={(e) => e.stopPropagation()} title={self ? "You can't disable your own account" : u.is_active ? "Turn off to block sign-in" : "Turn on to allow sign-in"}>
                        <Switch
                          checked={u.is_active}
                          disabled={self || setActive.isPending}
                          onChange={(v) => void toggleActive(u, v)}
                          label={<span className={u.is_active ? "ds-small" : "ds-small ds-muted"}>{u.is_active ? "Active" : "Disabled"}</span>}
                        />
                      </span>
                    );
                  },
                },
                {
                  key: "manage",
                  header: "",
                  align: "right",
                  width: 110,
                  cell: (u) => (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={ShieldCheck}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenId(u.id);
                      }}
                    >
                      Roles
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      <RolesDrawer
        user={open}
        onClose={() => setOpenId(null)}
        assignable={assignable}
        orgName={open ? orgName.get(open.entra_tenant_id) : undefined}
        isSelf={!!open && me?.id === open.id}
        canManage={canManageRoles}
        onToggleActive={toggleActive}
      />
    </div>
  );
}

function RolesDrawer({
  user,
  onClose,
  assignable,
  orgName,
  isSelf,
  canManage,
  onToggleActive,
}: {
  user: AdminUser | null;
  onClose: () => void;
  assignable: string[];
  orgName?: string;
  isSelf: boolean;
  canManage: boolean;
  onToggleActive: (u: AdminUser, active: boolean) => Promise<void>;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user) setSelected(new Set(user.roles));
    // Reset only when a different person is opened, not on every refetch.
  }, [user?.id]);

  const current = new Set(user?.roles ?? []);
  const toAdd = [...selected].filter((r) => !current.has(r));
  const toRemove = [...current].filter((r) => !selected.has(r) && r !== "device");
  const dirty = toAdd.length + toRemove.length > 0;

  const save = useMutation({
    mutationFn: async (u: AdminUser) => {
      for (const r of toAdd) await adminApi.assignRole(u.id, r);
      for (const r of toRemove) await adminApi.removeRole(u.id, r);
    },
    onSuccess: (_r, u) => {
      const now = sortRoles([...selected].filter((r) => r !== "device"));
      toast.success("Roles updated", now.length ? `${nameOf(u)}: ${now.map(roleLabel).join(", ")}.` : `${nameOf(u)} has no role now and can't see any reports.`);
      onClose();
    },
    onError: (e) => toast.error("Some roles couldn't be changed", errorText(e)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });

  const onSave = async () => {
    if (!user) return;
    if (isSelf && toRemove.includes("platform_admin")) {
      const ok = await confirm({
        title: "Remove your own platform admin role?",
        message: "You'll lose access to People & roles, Organizations and System health as soon as you save. Another platform admin would have to give it back.",
        confirmLabel: "Remove my admin role",
        tone: "danger",
      });
      if (!ok) return;
    }
    save.mutate(user);
  };

  const noneLeft = [...selected].filter((r) => r !== "device").length === 0;

  return (
    <Drawer
      open={!!user}
      onClose={onClose}
      title={user ? nameOf(user) : ""}
      footer={
        <>
          <Button onClick={onClose}>{dirty ? "Cancel" : "Close"}</Button>
          <Button variant="primary" loading={save.isPending} disabled={!dirty || !canManage} onClick={() => void onSave()}>
            Save changes
          </Button>
        </>
      }
    >
      {user ? (
        <div className="ds-stack" style={{ gap: 20 }}>
          <div className="ds-row" style={{ gap: 12 }}>
            <Avatar name={nameOf(user)} />
            <div style={{ minWidth: 0 }}>
              <div className="ds-truncate">{user.email}</div>
              <div className="ds-small ds-muted">
                {orgName ? `${orgName} · ` : ""}
                {user.last_login_at ? `last signed in ${formatRelative(user.last_login_at)}` : "last sign-in not recorded"}
              </div>
            </div>
          </div>

          <div>
            <div className="ds-label" style={{ marginBottom: 8 }}>What can {user.display_name?.split(" ")[0] || "they"} do?</div>
            {!canManage ? (
              <div style={{ marginBottom: 10 }}>
                <Banner tone="info">Only platform admins can change roles.</Banner>
              </div>
            ) : null}
            <div className="ds-stack" style={{ gap: 8 }}>
              {assignable.map((r) => (
                <div key={r} className="ds-card" style={{ padding: "10px 14px", borderColor: selected.has(r) ? "var(--accent)" : undefined }}>
                  <Checkbox
                    checked={selected.has(r)}
                    disabled={!canManage || save.isPending}
                    onChange={(v) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (v) next.add(r);
                        else next.delete(r);
                        return next;
                      })
                    }
                    label={
                      <span>
                        <span style={{ fontWeight: 600 }}>{roleLabel(r)}</span>
                        <span className="ds-small ds-muted" style={{ display: "block" }}>
                          {ROLE_INFO[r]?.description ?? "Custom role"}
                        </span>
                      </span>
                    }
                  />
                </div>
              ))}
            </div>
            {noneLeft ? (
              <div style={{ marginTop: 10 }}>
                <Banner tone="warning">Without a role, {nameOf(user)} can sign in but won't see any reports.</Banner>
              </div>
            ) : null}
          </div>

          <div>
            <div className="ds-label" style={{ marginBottom: 8 }}>Account</div>
            <div className="ds-card" style={{ padding: "10px 14px" }}>
              <Switch
                checked={user.is_active}
                disabled={isSelf}
                onChange={(v) => void onToggleActive(user, v)}
                label={
                  <span>
                    <span style={{ fontWeight: 600 }}>{user.is_active ? "Can sign in" : "Sign-in blocked"}</span>
                    <span className="ds-small ds-muted" style={{ display: "block" }}>
                      {isSelf ? "You can't disable your own account." : user.is_active ? "Turn off to block this person straight away." : "Turn on to let this person sign in again."}
                    </span>
                  </span>
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
